import * as XLSX from 'xlsx';
import { UserLeaveBalance } from 'api/newLeave/leaveHistory';

// Typed XLSX helpers to satisfy @typescript-eslint/no-unsafe-call (xlsx types are loose)
type WorkBook = XLSX.WorkBook;
type WorkSheet = XLSX.WorkSheet;
const xlsxUtils = XLSX.utils as {
  book_new: () => WorkBook;
  json_to_sheet: (data: unknown[]) => WorkSheet;
  book_append_sheet: (wb: WorkBook, ws: WorkSheet, name: string) => void;
};
const xlsxWriteFile = XLSX.writeFile as (wb: WorkBook, filename: string) => void;

const safeNum = (v: unknown) => {
  if (typeof v === "number") {
    return Number(v).toFixed(2);
  }
  if (typeof v === "string" && !Number.isNaN(Number(v))) {
    return Number(v).toFixed(2);
  }
  return v ?? "-";
};

const formatDate = (iso?: string | null) => {
  if (!iso || iso === '-') return "-";
  try {
    const d = iso.length === 10 ? new Date(`${iso}T00:00:00Z`) : new Date(iso);
    return d.toLocaleDateString();
  } catch (e) {
    return iso;
  }
};

const humanizeKey = (k = "") => {
  const map: Record<string, string> = {
    annualLeave: "Annual Leave",
    sickLeave: "Sick Leave",
    pressingNecessity: "Pressing Necessity",
    compassionate: "Compassionate",
    bereavement: "Bereavement",
    juryDuty: "Jury Duty",
    election: "Election",
    bandBusiness: "Band Business",
    governance: "Governance",
    education: "Education",
    professionalDevelopment: "Professional Development",
    culturalSpiritual: "Cultural / Spiritual",
  };
  if (map[k]) return map[k];

  return k
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^./, (s) => s.toUpperCase());
};

const getApproverNames = (approvals: unknown): string => {
  if (!approvals || !Array.isArray(approvals) || approvals.length === 0) {
    return "-";
  }

  const extractApprovalName = (approval: Record<string, unknown>): string | null => {
    if (typeof approval === "string") return approval as string;
    if (approval.name) return approval.name as string;
    if (approval.approvedBy) return approval.approvedBy as string;

    if (approval.approver && typeof approval.approver === "object") {
      const approver = approval.approver as Record<string, unknown>;
      const fn = approver.firstName || approver.first_name || approver.name;
      const ln = approver.lastName || approver.last_name;
      const full = [fn, ln].filter(Boolean).join(" ");
      return full || JSON.stringify(approver);
    }

    if (approval.user && typeof approval.user === "object") {
      const user = approval.user as Record<string, unknown>;
      const fn = user.firstName || user.first_name || user.name;
      const ln = user.lastName || user.last_name;
      const full = [fn, ln].filter(Boolean).join(" ");
      return full || JSON.stringify(user);
    }

    if (approval.firstName || approval.lastName) {
      const fn = approval.firstName as string;
      const ln = approval.lastName as string;
      return [fn, ln].filter(Boolean).join(" ");
    }

    if (approval.fullName || approval.full_name) {
      return (approval.fullName || approval.full_name) as string;
    }

    try {
      return JSON.stringify(approval);
    } catch (e) {
      return null;
    }
  };

  const names = (approvals as unknown[])
    .map((a: unknown) => {
      const approval = a as Record<string, unknown>;
      if (!approval && approval !== 0) return null;
      return extractApprovalName(approval);
    })
    .filter(Boolean);

  return names.length > 0 ? names.join(", ") : "-";
};

export const exportLeaveBalanceReport = (data: UserLeaveBalance[]) => {
  const workbook = xlsxUtils.book_new();

  // Sheet 1: Summary
  const summaryData = data.map((item) => ({
    "Employee Name": `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim(),
    "Start Date": formatDate(item.startDate),
    Department: item.department ?? "-",
    "Total Allocated": safeNum(item.totalAllocated),
    Used: safeNum(item.totalUsed),
    Pending: safeNum(item.totalPending),
    Balance: safeNum(item.totalBalance),
    "Leave Without Pay": safeNum(item.leaveWithoutPayUsed ?? "-"),
    "Carry Forward": typeof item.carryForward === "string" ? item.carryForward : (item.carryForward ?? "-"),
  }));

  const summarySheet = xlsxUtils.json_to_sheet(summaryData);
  (summarySheet as Record<string, unknown>).A1 = { t: 's', v: 'Leave Balance Summary Report', f: '' };
  xlsxUtils.book_append_sheet(workbook, summarySheet, "Summary");

  // Sheet 2: Leave History (All employees combined)
  const leaveHistoryData: Record<string, unknown>[] = [];
  data.forEach((item) => {
    const employeeName = `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim();

    if (item.leaveHistory && item.leaveHistory.length > 0) {
      item.leaveHistory.forEach((h: Record<string, unknown>) => {
        leaveHistoryData.push({
          "Employee Name": employeeName,
          Department: item.department ?? "-",
          "Leave Type": h.leaveType ?? "-",
          "From Date": formatDate((h.fromDate ?? h.from) as string),
          "To Date": formatDate((h.toDate ?? h.to) as string),
          "Number of Days": safeNum(h.numberOfDays),
          "Deduct Leave": h.deductLeave !== undefined ? safeNum(h.deductLeave) : "-",
          "Late Minutes": h.lateMinutes !== undefined ? String(h.lateMinutes) : "-",
          Status: h.status ?? "-",
          "Approved By": getApproverNames(h.approvals),
        });
      });
    }
  });

  const leaveHistorySheet = xlsxUtils.json_to_sheet(leaveHistoryData);
  xlsxUtils.book_append_sheet(workbook, leaveHistorySheet, "Leave History");

  // Sheet 3: Allocated Breakdown (All employees)
  const breakdownData: Record<string, unknown>[] = [];
  data.forEach((item) => {
    const employeeName = `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim();
    const breakdown = item.allocatedBreakdown ?? {};

    if (Object.keys(breakdown).length > 0) {
      const breakdownRow: Record<string, unknown> = {
        "Employee Name": employeeName,
        Department: item.department ?? "-",
      };

      Object.entries(breakdown).forEach(([k, v]) => {
        if (k !== "totalAllocated" && k !== "totalUsed") {
          breakdownRow[humanizeKey(k)] = safeNum(v);
        }
      });

      breakdownRow["Total Allocated"] = safeNum((breakdown as Record<string, unknown>).totalAllocated ?? 0);
      breakdownRow["Total Used"] = safeNum((breakdown as Record<string, unknown>).totalUsed ?? 0);

      breakdownData.push(breakdownRow);
    }
  });

  const breakdownSheet = xlsxUtils.json_to_sheet(breakdownData);
  xlsxUtils.book_append_sheet(workbook, breakdownSheet, "Allocated Breakdown");

  // Generate filename with timestamp
  const timestamp = new Date().toISOString().slice(0, 10);
  const filename = `Leave_Balance_Report_${timestamp}.xlsx`;

  // Write file
  xlsxWriteFile(workbook, filename);
};
